import { Children, createContext, isValidElement, useContext } from 'react';
import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { Icon } from './Icon.js';
import { Gauge } from './Gauge.js';

/**
 * Spec 231 — the stat row: N cells that touch, divided by vertical
 * hairlines. `--hub-stats-count` drives `.hub-stats`' grid column count (see
 * primitives.css) so a page varies N without a Tailwind grid class fighting
 * the archetype.
 *
 * Spec 234 finishes the stat cell with the gauge components.md's recipe
 * calls for: an optional `gaugeFraction` on `Stat` mounts a `<Gauge>` between
 * the note and the value. A cell that doesn't pass it renders no gauge at
 * all — it must not grow an empty dial.
 */
/**
 * Spec 248 — whether any cell in this row draws a gauge.
 *
 * Kridt: "Every cell in a row has the same anatomy — label, note, disc, gauge,
 * figure — even when one of them has no gauge to draw." Without this a cell
 * with no gauge simply collapsed, so the four Overview figures sat at four
 * different heights — the review's "four anatomies" — and the row read as four
 * widgets rather than one instrument. A gauged row now reserves the gauge's
 * space in every cell, and every figure lands on one baseline.
 */
const StatRowContext = createContext<{ gauged: boolean }>({ gauged: false });

export function StatRow({
  children,
  className,
  ...rest
}: { children: ReactNode; className?: string } & Record<string, unknown>) {
  const items = Children.toArray(children);

  // Dev-time guard for spec 231 §3.2: "at most one cell per row takes
  // colour, and only when the figure asks for action." Enforced here rather
  // than left to a reviewer scanning every call site. No `import.meta.env`
  // gate — this project has no vite/client ambient types wired up, and a
  // `console.warn` that only fires when a row actually misuses `attention`
  // costs nothing in production.
  const attentionCount = items.filter(
    (child): child is ReactElement<StatProps> =>
      isValidElement(child) && Boolean((child.props as Partial<StatProps>).attention),
  ).length;
  if (attentionCount > 1) {
    console.warn(
      `[StatRow] ${attentionCount} cells marked "attention" in one row — at most one per row ` +
        'should take the danger colour (spec 231 §3.2). A row where everything is coloured has ' +
        'told the reader nothing.',
    );
  }

  const gauged = items.some(
    (child) => isValidElement(child) && (child.props as Partial<StatProps>).gaugeFraction != null,
  );

  const style = { '--hub-stats-count': items.length } as CSSProperties;
  return (
    <StatRowContext.Provider value={{ gauged }}>
      <div className={`hub-stats${className ? ' ' + className : ''}`} style={style} data-gauged={gauged || undefined} {...rest}>
        {children}
      </div>
    </StatRowContext.Provider>
  );
}

export interface StatProps {
  /** What is measured — rendered at subtitle size. */
  label: string;
  /** A one-line note under the LABEL — "+6 vs yesterday" describes the
   *  measure, not the figure. */
  note?: ReactNode;
  /** The figure, at `--hub-text-figure`, in ink, with PROPORTIONAL digits —
   *  a lone figure never uses tabular numerals (spec 248, review P1-5). Tone
   *  belongs in `note`, never on the number, except `attention`. */
  value: ReactNode;
  /** Optional Boxicon name, shown in a disc beside the label. */
  icon?: string;
  /** At most one per row — colours the value with `--hub-color-danger`, and
   *  only when the figure genuinely asks for action. */
  attention?: boolean;
  /** 0–1: mounts a semicircle gauge between the note and the value (spec 234
   *  §5 / components.md's stat-cell recipe). Omit it and the cell renders
   *  exactly as before — no gauge grows where there is no fraction to show.
   *  When `attention` is also set, the gauge's arc takes the danger colour
   *  instead of ink, matching the value text. */
  gaugeFraction?: number;
  /** Spec 251: the gauge's two ends, labelled either side of the arc — for a
   *  scale that is not self-evident, like sentiment's −1..+1. */
  scaleEnds?: readonly [string, string];
  /** Spec 251: a signed gauge that fills from its centre (see Gauge). */
  diverging?: boolean;
  /** Extra content rendered below the figure — a trend badge, for instance. */
  children?: ReactNode;
  className?: string;
}

export function Stat({ label, note, value, icon, attention, gaugeFraction, scaleEnds, diverging, children, className, ...rest }: StatProps & Record<string, unknown>) {
  const { gauged } = useContext(StatRowContext);
  return (
    <div className={`hub-stat${attention ? ' hub-stat--attention' : ''}${className ? ' ' + className : ''}`} {...rest}>
      <div className="hub-stat__label">
        {icon ? (
          <span className="hub-disc">
            <Icon name={icon} />
          </span>
        ) : null}
        <span>{label}</span>
      </div>
      {/* Spec 248: the note slot always renders, so a cell without a note
          still has the anatomy and its figure stays on the row's baseline. */}
      <div className="hub-stat__note">{note ?? '\u00A0'}</div>
      {gauged ? (
        // Spec 251: in a gauged row the gauge and the figure are one readout,
        // the figure centred under its arc — it hung from the arc's left edge.
        // Scale ends sit BESIDE the arc's ends, not under them, so a labelled
        // gauge is no taller than a bare one and every figure keeps the row's
        // one baseline (248.A.1).
        <div className="hub-stat__readout">
          {scaleEnds ? <span className="hub-stat__end hub-stat__end--start">{scaleEnds[0]}</span> : null}
          {gaugeFraction != null ? (
            <Gauge fraction={gaugeFraction} attention={attention} diverging={diverging} className="hub-stat__gauge" />
          ) : (
            // Same viewBox and classes as Gauge, so it reserves exactly its box.
            <svg viewBox="0 0 120 68" className="w-full max-w-[120px] hub-stat__gauge" aria-hidden="true" data-gauge-placeholder="" />
          )}
          {scaleEnds ? <span className="hub-stat__end hub-stat__end--end">{scaleEnds[1]}</span> : null}
          <div className="hub-stat__value">{value}</div>
        </div>
      ) : (
        <div className="hub-stat__value">{value}</div>
      )}
      {children ?? null}
    </div>
  );
}
